/**
 * The one gate every file entering the chat composer passes through.
 *
 * Three entry points now stage files for a turn — the attach button, a drop
 * onto the composer, and a paste — and they have to agree on what is allowed.
 * The button used to own the only copy of the size + type check, so anything
 * else that wanted to stage a file had to restate it, and a restatement is how
 * the same bug ships twice (rule 7).
 *
 * The type check is `isAcceptedAttachment`, not a MIME-keyed lookup. Most
 * operating systems register no MIME type for research code (.R, .Rmd, .jl,
 * .do), so the browser reports an empty `file.type` and a MIME-only gate
 * rejects a researcher's script outright. That is also why the composer's
 * dropzone deliberately does NOT use react-dropzone's own `accept` map: it is
 * MIME-keyed and would drop exactly those files on the floor.
 */

import { isAcceptedAttachment } from '@bower/shared/schemas/upload.schema';

/**
 * Per-file cap for chat attachments.
 *
 * Deliberately NOT the shared `MAX_UPLOAD_BYTES` (500 MB). A read-only role's
 * chat files ride along with the message as multipart on send, so this cap is
 * a convention of the chat surface and always has been. A writer's files go
 * through the resumable upload, and this cap still applies to them: one cap on
 * both paths (decided 2026-10-01), so the role never changes what a researcher
 * may attach. Moved here verbatim from AttachmentButton so the three
 * entry points read one value — reconciling it with the shared cap is a
 * separate decision with backend implications, not a drive-by.
 */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/**
 * Ceiling on files staged for one turn.
 *
 * The attach button could never exceed this by much: a file picker selects
 * files, one dialog at a time. Dropping can: react-dropzone expands a dropped
 * DIRECTORY recursively via `webkitGetAsEntry`, so one drop of a project folder
 * stages every file beneath it, and the agent service reads each staged file
 * into memory on send. That vector arrived with drop, so the bound belongs with
 * it. Bounds COUNT only, deliberately: the per-file cap is unchanged and
 * reworking the size policy is not this change's job.
 *
 * Exported for the parity test with the agent's `MAX_ATTACHMENT_REFS`, which
 * rejects a turn carrying more ids than this.
 */
export const MAX_ATTACHMENTS_PER_TURN = 10;

const MAX_ATTACHMENT_LABEL = `${Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)}MB`;

export interface AttachmentPick {
  /** Files that passed both gates, in the order they were given. */
  readonly accepted: File[];
  /** The files that did not, positionally matched to `errors`. */
  readonly rejected: File[];
  /** One human-readable line per rejected file. Empty when nothing was cut. */
  readonly errors: string[];
}

/**
 * Split a set of candidate files into what may be staged and what may not.
 *
 * Returns rather than throws, and never silently drops: every rejected file
 * comes back with a line saying why, so the caller has no way to fail quiet
 * (rule 12). Callers are expected to surface `errors` — the button's
 * rejections went nowhere before this, so an oversize file simply vanished.
 */
export function pickAttachments(files: readonly File[]): AttachmentPick {
  const accepted: File[] = [];
  const rejected: File[] = [];
  const errors: string[] = [];

  for (const file of files) {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      rejected.push(file);
      errors.push(`${file.name}: File size exceeds ${MAX_ATTACHMENT_LABEL}`);
      continue;
    }

    if (!isAcceptedAttachment(file.name, file.type)) {
      rejected.push(file);
      errors.push(`${file.name}: File type not allowed`);
      continue;
    }

    accepted.push(file);
  }

  return { accepted, rejected, errors };
}

/** Reasons shown in full before the toast summarises the remainder. */
const MAX_SHOWN_REJECTIONS = 3;

/**
 * One toast line for a set of rejections, bounded.
 *
 * `pickAttachments` runs over every candidate, and a recursive directory drop
 * can produce hundreds — so joining them all put 400 reasons and ~14.7k
 * characters on a single line. The per-turn staging cap does not help here: it
 * bounds what is ACCEPTED, and these are the files that were not.
 */
export function describeRejections(errors: readonly string[]): string {
  const shown = errors.slice(0, MAX_SHOWN_REJECTIONS).join('; ');
  const hidden = errors.length - MAX_SHOWN_REJECTIONS;
  return hidden > 0 ? `${shown} — and ${hidden} more` : shown;
}
